import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { useShop } from "@/contexts/ShopContext";
import { toast } from "sonner";

export default function Delivery() {
  const { currentShopId } = useShop();

  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [deliveredCount, setDeliveredCount] = useState(0);

  // ==========================================================
  // LOAD DELIVERY QUEUE
  // ==========================================================

  const load = useCallback(async () => {
    if (!currentShopId) {
      setTickets([]);
      return;
    }

    try {
      const { data } = await api.get(
        "/delivery",
        {
          params: {
            shop_id: currentShopId,
          },
        }
      );

      setTickets(data || []);

    } catch (error) {
      console.error(
        "Failed to load delivery queue:",
        error
      );

      toast.error(
        error.response?.data?.detail ||
          "Failed to load delivery orders"
      );
    }
  }, [currentShopId]);

  // ==========================================================
  // INITIAL LOAD
  // ==========================================================

  useEffect(() => {
    load();
  }, [load]);

  // ==========================================================
  // AUTO REFRESH
  // ==========================================================

  useEffect(() => {
    const id = setInterval(
      load,
      8000
    );

    return () => clearInterval(id);
  }, [load]);

  // ==========================================================
  // RESET DELIVERED COUNT WHEN SHOP CHANGES
  // ==========================================================

  useEffect(() => {
    setDeliveredCount(0);
  }, [currentShopId]);

  // ==========================================================
  // DELIVER ONE UNIT
  // ==========================================================

  const deliver = async (ticket) => {
    try {
      setLoading(true);

      await api.post(
        "/delivery/deliver",
        {
          order_id:
            ticket.order_id,

          item_id:
            ticket.item_id,
        }
      );

      toast.success(
        `${ticket.name} from Order #${ticket.order_number} delivered`
      );

      setDeliveredCount((c) => c + 1);

      await load();

    } catch (error) {
      console.error(
        "Failed to deliver item:",
        error
      );

      toast.error(
        error.response?.data?.detail ||
          "Failed to deliver item"
      );
    } finally {
      setLoading(false);
    }
  };

  // ==========================================================
  // UI
  // ==========================================================

  return (
    <section
      className="view"
      data-testid="delivery-page"
    >

      {/* ====================================================
          RESPONSIVE STYLES
          Inline styles can't use media queries, so the ticket
          row's layout lives here instead. Below 680px the
          fixed-width columns (90+150+130+120px) no longer fit
          the viewport, which was pushing the Delivery button
          off-screen — this switches the row to a single
          stacked column so every part, including the button,
          stays visible and full-width.
      ===================================================== */}

      <style>{`
        .delivery-ticket {
          display: grid;
          grid-template-columns: 90px 1fr 150px 130px 120px;
          align-items: center;
          gap: 16px;
          padding: 14px 16px;
          border: 1px solid var(--border, #ddd);
          border-radius: 10px;
          background: var(--surface, #fff);
        }

        @media (max-width: 680px) {
          .delivery-ticket {
            grid-template-columns: 1fr;
            gap: 10px;
          }

          .delivery-ticket .dt-delivery {
            text-align: left;
          }

          .delivery-ticket .dt-delivery button {
            width: 100%;
          }
        }
      `}</style>

      {/* ====================================================
          HEADER
      ===================================================== */}

      <div className="page-head">

        <div>

          <div className="eyebrow">
            Handoff · ready now
          </div>

          <h1>
            Delivery
          </h1>

          <p>
            Each ready item is listed separately
            and can be delivered immediately.
          </p>

        </div>

        <div
          style={{
            display: "flex",
            gap: "16px",
            alignItems: "center",
          }}
        >

          <div
            className="stat-note mono"
            data-testid="ready-count"
          >
            {tickets.length} items ready
          </div>

          <div
            className="stat-note mono"
            data-testid="delivered-count"
          >
            {deliveredCount} delivered
          </div>

        </div>

      </div>

      {/* ====================================================
          DELIVERY LIST
      ===================================================== */}

      <div className="card">

        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "8px",
            padding: "16px",
          }}
        >

          {tickets.map(
            (ticket) => (

              <div
                key={`${ticket.order_id}-${ticket.item_id}-${ticket.unit_number}`}
                className="delivery-ticket"
              >

                {/* ORDER */}

                <div>

                  <div
                    style={{
                      fontSize: "10px",
                      textTransform:
                        "uppercase",
                      color:
                        "var(--fg-muted)",
                      fontWeight: 700,
                    }}
                  >
                    Order
                  </div>

                  <strong
                    className="mono"
                    style={{
                      fontSize: "17px",
                    }}
                  >
                    #{ticket.order_number}
                  </strong>

                </div>

                {/* ITEM + CUSTOMER (inline) */}

                <div
                  style={{
                    display: "flex",
                    alignItems: "baseline",
                    gap: "10px",
                    flexWrap: "wrap",
                  }}
                >

                  <div
                    style={{
                      fontWeight: 800,
                      fontSize: "15px",
                    }}
                  >
                    {ticket.name}
                  </div>

                  <div
                    style={{
                      fontSize: "15px",
                      fontWeight: 600,
                      color: "var(--fg-muted)",
                      marginLeft: "30px",
                    }}
                  >
                    {ticket.customer_name}
                  </div>

                </div>

                {/* CATEGORY */}

                <div>

                  <span
                    className="status status-info"
                  >
                    {ticket.category}
                  </span>

                  <div
                    style={{
                      marginTop: "4px",
                      fontSize: "11px",
                      color:
                        "var(--fg-muted)",
                    }}
                  >
                    Unit #{ticket.unit_number}
                  </div>

                </div>

                {/* READY */}

                <div>

                  <span
                    className="status status-success"
                  >
                    Ready
                  </span>

                </div>

                {/* DELIVERY */}

                <div className="dt-delivery">

                  <button
                    type="button"
                    className="btn btn-primary btn-sm"
                    onClick={() =>
                      deliver(ticket)
                    }
                    disabled={loading}
                    data-testid={`deliver-item-${ticket.order_number}-${ticket.item_id}-${ticket.unit_number}`}
                  >
                    Delivery
                  </button>

                </div>

              </div>

            )
          )}

          {tickets.length === 0 && (

            <div
              className="empty"
              style={{
                margin: "20px",
                padding: "40px",
                textAlign: "center",
              }}
            >
              Nothing waiting for handoff
              right now
            </div>

          )}

        </div>

        <div className="note">
          Each prepared Food or Drinks unit appears
          separately. Misc items bypass Kitchen and
          appear here immediately.
        </div>

      </div>

    </section>
  );
}